import { createPortal } from 'react-dom';
import { useToast, NotificationType } from './useToast';

// Simple Icons (No external lib)
const Icons = {
  success: (
    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

const getColors = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return 'bg-white border-l-4 border-green-500';
    case 'error':
      return 'bg-white border-l-4 border-red-500';
    case 'warning':
      return 'bg-white border-l-4 border-yellow-500';
    case 'info':
      return 'bg-white border-l-4 border-blue-500';
  }
};

export const ToastRoot = () => {
  const { toasts, removeNotification } = useToast();

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-[60] flex flex-col gap-3 w-full max-w-sm pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto transform transition-all duration-300 ease-in-out hover:scale-[1.02] shadow-lg rounded-md p-4 flex items-start gap-3 ${getColors(toast.type)}`}
          role="alert"
        >
          <div className="flex-shrink-0 mt-0.5">{Icons[toast.type]}</div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900">{toast.title}</h4>
            {toast.message && <p className="text-sm text-gray-600 mt-1">{toast.message}</p>}
          </div>
          <button
            onClick={() => removeNotification(toast.id)}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close notification"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};
